// Per-tournament deck preferences. Stored client-side in localStorage
// keyed by tournament DEF id (not instance id) so the choice persists
// across recurring cycles — e.g. the Weekly Ladder picks the same deck
// every week unless the player changes it.
//
// We store the deck's stable server-assigned id rather than its slot
// index so reorderings, additions, and deletions don't silently move
// the preference to a different deck. The caller resolves the id back
// to a slot index via `playerData.deckIds`; if the deck is gone (or
// was the unsaved placeholder ''), fall back to the player's currently-
// active deck.
//
// Cross-device sync would require a server-side table; until that's
// requested, localStorage is good enough for single-device play.

const KEY_PREFIX = 'cardverse:tournamentDeck:';

export function getTournamentDeckId(defId: string): string | null {
  try {
    return localStorage.getItem(KEY_PREFIX + defId);
  } catch {
    return null;
  }
}

export function setTournamentDeckId(defId: string, deckId: string): void {
  try {
    localStorage.setItem(KEY_PREFIX + defId, deckId);
  } catch {
    // Quota exceeded / private mode / etc. — preference is non-essential,
    // so just drop it silently.
  }
}

export function clearTournamentDeckId(defId: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + defId);
  } catch { /* see note in setTournamentDeckId */ }
}

/** Resolve the saved preference to a slot index in
 *  `playerData.deckIds`, falling back to a fallback index when the
 *  saved deck has been deleted or the player never chose one. */
export function resolveTournamentDeckIndex(
  defId: string,
  deckIds: readonly string[],
  fallbackIndex: number,
): number {
  const savedId = getTournamentDeckId(defId);
  if (!savedId) return fallbackIndex;
  const idx = deckIds.indexOf(savedId);
  if (idx < 0) return fallbackIndex;
  return idx;
}

/** Render the inline deck-picker dropdown used on tournament cards
 *  (list view), the detail page, and the main-menu widget. Selecting
 *  an option saves the choice via `setTournamentDeckId`; the caller
 *  is responsible for binding the change handler since each surface
 *  hooks into a different event lifecycle. */
export function renderTournamentDeckPicker(
  defId: string,
  playerData: { decks: { name: string }[]; deckIds: string[]; activeDeckIndex: number },
): string {
  if (playerData.decks.length === 0) return '';
  const savedId = getTournamentDeckId(defId);
  let selectedIndex = playerData.activeDeckIndex;
  if (savedId) {
    const idx = playerData.deckIds.indexOf(savedId);
    if (idx >= 0) selectedIndex = idx;
  }
  const options = playerData.decks.map((deck, i) => {
    // Decks are validated at play time, so we can omit the "(N/50)"
    // count from the label and keep the dropdown compact. A 0/50
    // deck will simply trip the deck-rule validator when the player
    // tries to use it.
    const id = playerData.deckIds[i] || `__index:${i}`;
    return `<option value="${escapeAttr(id)}" ${i === selectedIndex ? 'selected' : ''}>${escapeText(deck.name)}</option>`;
  }).join('');
  return `
    <label class="tournament-deck-picker">
      <span class="tournament-deck-picker-label">Deck:</span>
      <select>${options}</select>
    </label>
  `;
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}
